import type { LogoVariantProps } from "@kirvanobr/theme";

import { HTMLUIProps, mapPropsVariants, PropGetter } from "@kirvanobr/system";
import { logo } from "@kirvanobr/theme";
import { ReactRef, useDOMRef } from "@kirvanobr/react-utils";
import { useMemo } from "react";
import { clsx, dataAttr } from "@kirvanobr/shared-utils";

export interface Props extends Partial<HTMLUIProps<"svg">> {
  /**
   * Ref to the DOM node.
   */
  ref?: ReactRef<HTMLElement>;
}

export type UseLogoProps = Props & LogoVariantProps;

export function useLogo(originalProps: UseLogoProps) {
  const [props, variantProps] = mapPropsVariants(originalProps, logo.variantKeys);

  const { ref, className, variant, ...otherProps } = originalProps;

  const domRef = useDOMRef(ref);

  const styles = useMemo(
    () =>
      logo({
        ...variantProps,
        className,
        variant,
      }),
    [...Object.values(variantProps), className, variant],
  );

  const getLogoProps: PropGetter = (props = {}) => ({
    ...props,
    ...otherProps,
    "aria-hidden": dataAttr(true),
    className: clsx(styles, props.className),
    style: {
      ...props.style,
      ...otherProps.style,
    },
  });

  return { props, styles, domRef, variant, getLogoProps, ...otherProps };
}

export type UseLogoReturn = ReturnType<typeof useLogo>;
